import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormFieldBaseComponent } from '../form-field-base/form-field-base.component';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { CoreComponentService, Style } from '@app/core/services/core-component.service';
import { GraphMailService } from '@app/modules/microsoft-graph/services/graph-mail.service';
import { LayoutService } from '@app/core/services/layout.service';
import { TranslateService } from '@app/core/services/translate.service';
import { RememberService } from '@app/core/services/remember.service';
import { UtilityService } from '@app/core/services/utility.service';
import { MatDialog } from '@angular/material/dialog';
import { ToastService } from '@app/core/services/toast.service';
import type { Attachment, FileAttachment, ItemAttachment, Message, ReferenceAttachment } from '@microsoft/microsoft-graph-types';
import { GraphAuthService } from '@app/modules/microsoft-graph/auth/services/graph-auth.service';
import { Subject, skip, takeUntil } from 'rxjs';

type FileUploaderDisplayRow = FileUploaderFileRow | FileUploaderOutlookMailRow | FileUploaderOutlookAttachmentRow;
type FileUploaderAttachment = Attachment | FileAttachment | ItemAttachment | ReferenceAttachment;
export type FileUploaderGraphMessage = Message;
export type FileUploaderValue = File | FileUploaderGraphMessage;

@Component({
    selector: 'tt-file-uploader',
    templateUrl: './file-uploader.component.html',
    styleUrls: ['./file-uploader.component.css'],
})
export class FileUploaderComponent<TValue extends FileUploaderValue = File> extends FormFieldBaseComponent implements OnDestroy {
    /**
     * Fileuploader does not support remember.
     */
    public override ttRememberId?: undefined;

    /**
     * Fileuploader does not support remember.
     */
    public override set ttOnlyRemember(_value: boolean) {}
    public override get ttOnlyRemember(): true {
        return true;
    }

    /**
     * Whether to allow multiple files to imported or not.
     */
    @Input()
    get ttMultiple(): boolean {
        return this._multiple;
    }
    set ttMultiple(value: BooleanInput) {
        this._multiple = coerceBooleanProperty(value);
    }
    private _multiple: boolean = false;

    /**
     * Whether the file uploader should display a drop-zone instead of the default button.
     */
    @Input()
    get ttDropZone(): boolean {
        return this._dropzone;
    }
    set ttDropZone(value: BooleanInput) {
        this._dropzone = coerceBooleanProperty(value);
    }
    private _dropzone: boolean = false;

    /**
     * Whether to allow Outlook email drops to be loaded as Graph messages.
     */
    @Input()
    get ttAllowEmails(): boolean {
        return this._allowEmails;
    }
    set ttAllowEmails(value: BooleanInput) {
        this._allowEmails = coerceBooleanProperty(value);
    }
    private _allowEmails: boolean = false;

    /**
     * Comma seperated string containing what file types to accept, can be file extension starting with . like `'.jpg, .png'`., a MIME type or `'image/*'`, `'audio/*'`, `'video/*'`.
     * If null or not defined will indicate any file type.
     */
    @Input()
    public ttAccept?: string | null;

    /**
     * **Reactive** The selected files in the file uploader.
     */
    @Input()
    ttFiles: TValue[] = [];
    files: File[] = [];

    private outlookMailRows: FileUploaderOutlookMailRow[] = [];
    private outlookAttachmentRows: FileUploaderOutlookAttachmentRow[] = [];
    private outlookAttachmentFileMetadata: FileUploaderOutlookAttachmentFileMetadata[] = [];
    private destroy$ = new Subject<void>();

    /**
     * Event emitted when there are changes in the selected files.
     */
    @Output()
    ttFilesChange = new EventEmitter<TValue[]>();

    /**
     * The input field of type file.
     */
    @ViewChild('inputRef') inputRef?: ElementRef;

    /**
     * Ids for elements in the component.
     */
    public id = {
        input: crypto.randomUUID(),
    };

    /**
     * Translations for words used in the component.
     */
    public filetranslations: { [key: string]: string } = {
        select_file: '',
        select_files: '',
        drag_and_drop_or_click: '',
    };

    public get displayRows(): FileUploaderDisplayRow[] {
        return [...this.files.map((file) => <FileUploaderFileRow>{ kind: 'file', file: file }), ...this.outlookMailRows, ...this.outlookAttachmentRows];
    }

    /**
     * Styling for elements in the component.
     */
    override style: Style = {
        dropzone: {},
        dropzone_label: {},
    };

    fileTranslations = {
        no_account_error: 'Ingen konto funnet, har du husket å logge på',
        invalid_user_error: 'Kontoen din har ikke tilgang til innhold i denne e-posten',
    };

    constructor(
        layout: LayoutService,
        coreComponent: CoreComponentService,
        translate: TranslateService,
        remember: RememberService,
        utility: UtilityService,
        dialog: MatDialog,
        toast: ToastService,
        private graphMail: GraphMailService,
        private graphAuth: GraphAuthService,
    ) {
        super(layout, coreComponent, translate, remember, utility, dialog, toast);
        this.graphAuth.account$.pipe(skip(1), takeUntil(this.destroy$)).subscribe({
            next: (account) => {
                if (!!account) {
                    this.retryFailedOutlookMailRows();
                }
            },
        });
    }

    /**
     * Sets the selected file/files from the event to the list.
     *
     * @param event the change event of an input field of type file.
     */
    public onFileChanged(event: Event) {
        if (!!event?.target && (event.target as HTMLInputElement)?.files && (event.target as HTMLInputElement).files !== null) {
            const files = Array.from((event.target as HTMLInputElement).files!).filter((file) => this.isFileTypeAccepted(file));

            if (files.length < 1) {
                return;
            }

            if (!this._multiple) {
                this.outlookMailRows = [];
                this.outlookAttachmentRows = [];
                this.outlookAttachmentFileMetadata = [];
            }

            this.updateFilesList(files);
            this.emitFilesChange();
        }
    }

    /**
     * Handles dragover events over the dropzone.
     *
     * @param event the dragevent from dragover.
     */
    public onDragover(event: DragEvent) {
        event.preventDefault();

        this.style['dropzone'].outline = 'solid 0.2rem var(--tt-primary-color)';
        this.style['dropzone'].outlineOffset = '-0.2rem';
    }

    /**
     * Handles dragleave events from the dropzone.
     *
     * @param event the dragevent from the dragleave.
     */
    public onDragleave(event: DragEvent) {
        event.preventDefault();

        delete this.style['dropzone'].outline;
        delete this.style['dropzone'].outlineOffset;
    }

    /**
     * Handles file drop in the drop zone.
     *
     * @param event the dragevent from the file drop.
     */
    public async onFilesDropped(event: DragEvent) {
        event.preventDefault();

        try {
            if (event?.dataTransfer) {
                const droppedItems = await this.getAcceptedDroppedItems(event.dataTransfer);
                this.applyDroppedItems(droppedItems);
            }
        } finally {
            delete this.style['dropzone'].outline;
            delete this.style['dropzone'].outlineOffset;
        }
    }

    public trackDisplayRow(_index: number, row: FileUploaderDisplayRow): string {
        if (row.kind === 'file') {
            return `file:${row.file.name}`;
        }

        return row.rowId;
    }

    public getDisplayRowLabel(row: FileUploaderDisplayRow): string {
        if (row.kind === 'file') {
            return row.file.name;
        }

        if (row.kind === 'outlook-attachment') {
            return row.name;
        }

        if (row.status === 'error') {
            return `${this.getOutlookMailRowSubject(row, 'Outlook email')}`;
        }

        if (row.status === 'loaded') {
            return this.getOutlookMailRowSubject(row, '(no subject)');
        }

        return this.getOutlookMailRowSubject(row, 'Outlook email');
    }

    public getDisplayRowTooltip(row: FileUploaderDisplayRow): string {
        return `${this.getDisplayRowLabel(row)} ${row.kind !== 'file' ? (row.errorMessage ?? '') : ''}`;
    }

    public getDisplayRowStatusIcon(row: FileUploaderDisplayRow): string {
        if (row.kind === 'file') {
            return 'fal fa-file';
        }

        if (row.status === 'loading') {
            return 'fas fa-spin fa-spinner-third';
        }

        if (row.status === 'error') {
            return 'fas fa-exclamation-triangle';
        }

        if (row.kind === 'outlook-mail') {
            return 'fal fa-envelope';
        }

        if (row.kind === 'outlook-attachment') {
            return 'far fa-paperclip';
        }

        return '';
    }

    public hasOutlookMailAttachmentRows(row: FileUploaderDisplayRow): row is FileUploaderOutlookMailRow {
        return row.kind === 'outlook-mail' && !!row.attachments?.length;
    }

    public trackOutlookMailAttachmentRow(_index: number, row: FileUploaderOutlookMailAttachmentRow): string {
        return row.rowId;
    }

    public getOutlookMailAttachmentLabel(row: FileUploaderOutlookMailAttachmentRow): string {
        return row.name;
    }

    public isDisplayRowError(row: FileUploaderDisplayRow): boolean {
        return row.kind !== 'file' && row.status === 'error';
    }

    public removeDisplayRow(row: FileUploaderDisplayRow) {
        if (row.kind === 'file') {
            this.removeFile(row.file);
            return;
        }

        if (row.kind === 'outlook-mail') {
            this.removeOutlookMailRow(row);
            return;
        }

        this.removeOutlookAttachmentRow(row);
    }

    public removeOutlookMailAttachment(row: FileUploaderOutlookMailRow, attachmentRow: FileUploaderOutlookMailAttachmentRow) {
        const currentRow = this.getOutlookMailRow(row.rowId);

        if (!currentRow?.message || !currentRow.attachments) {
            return;
        }

        const attachmentIndex = currentRow.attachments.findIndex((rowAttachment) => rowAttachment.rowId === attachmentRow.rowId);

        if (attachmentIndex < 0) {
            return;
        }

        const removedAttachmentRow = currentRow.attachments[attachmentIndex];
        removedAttachmentRow.removed = true;
        this.syncOutlookMailMessageAttachments(currentRow);
        this.emitFilesChange();
    }

    public restoreOutlookMailAttachment(row: FileUploaderOutlookMailRow, attachmentRow: FileUploaderOutlookMailAttachmentRow) {
        const currentRow = this.getOutlookMailRow(row.rowId);

        if (!currentRow?.message || !currentRow.attachments) {
            return;
        }

        const currentAttachmentRow = currentRow.attachments.find((rowAttachment) => rowAttachment.rowId === attachmentRow.rowId);

        if (!currentAttachmentRow?.removed) {
            return;
        }

        currentAttachmentRow.removed = false;
        this.syncOutlookMailMessageAttachments(currentRow);
        this.emitFilesChange();
    }

    /**
     * Checks whether the given file has a file type which is accepted.
     *
     * @param file the file to check if is accepted.
     * @returns `true` if the given file's file type is accepted, `false` if not.
     */
    private isFileTypeAccepted(file: File): boolean {
        if (!this.ttAccept) {
            return true;
        }
        const acceptedFileTypesArray = this.ttAccept.split(',').map((type) => type.trim());
        return acceptedFileTypesArray.some((type) => {
            if (type === '*/*') {
                return true;
            }

            if (type.endsWith('/*')) {
                const baseType = type.replace('/*', '');
                return file.type.startsWith(baseType);
            }

            return file.type === type || file.name.toLowerCase().endsWith(type.toLowerCase());
        });
    }

    private async getAcceptedDroppedItems(dataTransfer: DataTransfer): Promise<FileUploaderDroppedItem[]> {
        const droppedItems: FileUploaderDroppedItem[] = [];
        const fileItems = this.getAcceptedDataTransferFiles(dataTransfer);
        let disabledOutlookEmailDropped = false;

        for (let item of this.getDataTransferItems(dataTransfer)) {
            if (item.kind !== 'string') {
                continue;
            }

            const text = await this.getStringFromDataTransferItem(item);
            const outlookPayload = this.parseOutlookDroppedPayload(text);

            if (!!outlookPayload && this.isOutlookDroppedMailPayload(outlookPayload)) {
                if (!this._allowEmails) {
                    disabledOutlookEmailDropped = true;
                    continue;
                }

                const userId = this.getOutlookMailboxUserId(outlookPayload);
                const subjects = this.getOutlookDroppedMailSubjects(outlookPayload);
                droppedItems.push(...this.getOutlookDroppedMailItemIds(outlookPayload).map((sourceId, index) => <FileUploaderDroppedItem>{ kind: 'outlook-mail', sourceId: sourceId, userId: userId, subject: subjects[index] }));
            } else if (!!outlookPayload && this.isOutlookDroppedAttachmentPayload(outlookPayload)) {
                const userId = this.getOutlookMailboxUserId(outlookPayload);
                droppedItems.push(...this.getOutlookDroppedAttachmentItems(outlookPayload, userId));
            }
        }

        droppedItems.unshift(...fileItems.filter((file) => !(disabledOutlookEmailDropped && this.isOutlookMessageFile(file))).map((file) => ({ kind: 'file' as const, file: file })));

        return droppedItems;
    }

    private getAcceptedDataTransferFiles(dataTransfer: DataTransfer): File[] {
        return this.getUniqueFiles([...this.getFilesFromDataTransferItems(dataTransfer), ...Array.from(dataTransfer.files ?? [])]).filter((file) => this.isFileTypeAccepted(file));
    }

    private getFilesFromDataTransferItems(dataTransfer: DataTransfer): File[] {
        return this.getDataTransferItems(dataTransfer)
            .filter((item) => item.kind === 'file')
            .map((item) => item.getAsFile())
            .filter((file): file is File => !!file);
    }

    private getDataTransferItems(dataTransfer: DataTransfer): DataTransferItem[] {
        return Array.from(dataTransfer.items ?? []);
    }

    private getUniqueFiles(files: File[]): File[] {
        const uniqueFiles: File[] = [];

        for (let file of files) {
            if (!uniqueFiles.some((uniqueFile) => this.isSameFile(uniqueFile, file))) {
                uniqueFiles.push(file);
            }
        }

        return uniqueFiles;
    }

    private isSameFile(first: File, second: File): boolean {
        return first === second || (first.name === second.name && first.size === second.size && first.type === second.type && first.lastModified === second.lastModified);
    }

    private isOutlookMessageFile(file: File): boolean {
        return file.type === 'application/vnd.ms-outlook' || file.name.trim().toLocaleLowerCase().endsWith('.msg');
    }

    private getStringFromDataTransferItem(item: DataTransferItem): Promise<string | null> {
        return new Promise((resolve) => item.getAsString((text) => resolve(text)));
    }

    private parseOutlookDroppedPayload(text: string | null): OutlookDroppedPayload | null {
        if (!text) {
            return null;
        }

        try {
            const payload: unknown = JSON.parse(text);

            if (this.isOutlookDroppedMailPayload(payload) || this.isOutlookDroppedAttachmentPayload(payload)) {
                return payload;
            }

            return null;
        } catch {
            return null;
        }
    }

    private isOutlookDroppedMailPayload(payload: unknown): payload is OutlookDroppedMailPayload {
        if (!payload || typeof payload !== 'object') {
            return false;
        }

        const itemIds = this.getOutlookDroppedMailItemIds(payload as OutlookDroppedMailPayload);
        return Array.isArray(itemIds) && itemIds.length > 0 && itemIds.every((itemId) => typeof itemId === 'string' && itemId.trim().length > 0);
    }

    private isOutlookDroppedAttachmentPayload(payload: unknown): payload is OutlookDroppedAttachmentPayload {
        if (!payload || typeof payload !== 'object') {
            return false;
        }

        const attachmentPayload = payload as Partial<OutlookDroppedAttachmentPayload>;

        return attachmentPayload.itemType === 'attachment' && Array.isArray(attachmentPayload.attachmentFiles) && attachmentPayload.attachmentFiles.length > 0 && attachmentPayload.attachmentFiles.every((attachment) => this.isOutlookDroppedAttachmentFile(attachment));
    }

    private isOutlookDroppedAttachmentFile(attachment: unknown): attachment is OutlookDroppedAttachmentFile {
        if (!attachment || typeof attachment !== 'object') {
            return false;
        }

        const attachmentFile = attachment as Partial<OutlookDroppedAttachmentFile>;

        return typeof attachmentFile.attachmentItemId === 'string' && attachmentFile.attachmentItemId.trim().length > 0 && typeof attachmentFile.name === 'string' && attachmentFile.name.trim().length > 0;
    }

    private getOutlookDroppedMailItemIds(payload: Partial<OutlookDroppedMailPayload>): string[] {
        if (Array.isArray(payload.latestItemIds) && payload.latestItemIds.length > 0) {
            return payload.latestItemIds;
        }

        if (Array.isArray(payload.itemIds) && payload.itemIds.length > 0) {
            return payload.itemIds;
        }

        return [];
    }

    private getOutlookDroppedMailSubjects(payload: Partial<OutlookDroppedMailPayload>): string[] {
        if (!Array.isArray(payload.subjects)) {
            return [];
        }

        return payload.subjects.map((subject) => (typeof subject === 'string' ? subject.trim() : ''));
    }

    private getOutlookDroppedAttachmentItems(payload: OutlookDroppedAttachmentPayload, userId?: string): FileUploaderDroppedItem[] {
        return payload.attachmentFiles.map((attachmentFile) => ({
            kind: 'outlook-attachment',
            attachmentItemId: attachmentFile.attachmentItemId,
            name: attachmentFile.name,
            size: typeof attachmentFile.size === 'number' ? attachmentFile.size : undefined,
            contentType: this.firstNonEmptyString(attachmentFile.ContentType, attachmentFile.contentType),
            userId: userId,
        }));
    }

    private getOutlookMailboxUserId(payload: OutlookDroppedPayload): string | undefined {
        const mailboxInfo = this.getOutlookMailboxInfo(payload);

        return this.firstNonEmptyString(mailboxInfo?.mailboxSmtpAddress, mailboxInfo?.userIdentity, mailboxInfo?.sourceId);
    }

    private getOutlookMailboxInfo(payload: OutlookDroppedPayload): Partial<OutlookMailboxInfo> | undefined {
        if (!!payload.itemData?.mailboxInfo && typeof payload.itemData.mailboxInfo === 'object') {
            return payload.itemData.mailboxInfo;
        }

        return payload.mailboxInfos?.find((mailboxInfo) => !!mailboxInfo && typeof mailboxInfo === 'object');
    }

    private firstNonEmptyString(...values: (string | undefined)[]): string | undefined {
        return values.find((value) => typeof value === 'string' && value.trim().length > 0)?.trim();
    }

    private applyDroppedItems(droppedItems: FileUploaderDroppedItem[]) {
        if (droppedItems.length < 1) {
            return;
        }

        const selectedItems = this._multiple ? droppedItems : [this.getSingleDroppedItem(droppedItems)];
        let selectedValuesChanged = false;

        if (!this._multiple) {
            selectedValuesChanged = this.clearSelectedItems();
        }

        const files = selectedItems.filter((item): item is { kind: 'file'; file: File } => item.kind === 'file').map((item) => item.file);
        const outlookItems = selectedItems.filter((item): item is { kind: 'outlook-mail'; sourceId: string; userId?: string; subject?: string } => item.kind === 'outlook-mail');
        const outlookAttachmentItems = selectedItems.filter((item): item is FileUploaderDroppedOutlookAttachmentItem => item.kind === 'outlook-attachment');

        if (files.length > 0) {
            this.updateFilesList(files);
            selectedValuesChanged = true;
        }

        const outlookRows = this.addOutlookMailRows(outlookItems);
        const outlookAttachmentRows = this.addOutlookAttachmentRows(outlookAttachmentItems);

        if (selectedValuesChanged) {
            this.emitFilesChange();
        }

        if (outlookRows.length > 0) {
            void this.loadOutlookMailRows(outlookRows);
        }

        if (outlookAttachmentRows.length > 0) {
            void this.loadOutlookAttachmentRows(outlookAttachmentRows);
        }
    }

    private getSingleDroppedItem(droppedItems: FileUploaderDroppedItem[]): FileUploaderDroppedItem {
        return droppedItems.find((item) => item.kind !== 'file') ?? droppedItems[0];
    }

    private clearSelectedItems(): boolean {
        const hadSelectedValues = this.getSelectedValues().length > 0;

        this.files = [];
        this.outlookMailRows = [];
        this.outlookAttachmentRows = [];
        this.outlookAttachmentFileMetadata = [];
        this.resetFileInput();

        return hadSelectedValues;
    }

    private addOutlookMailRows(items: { kind: 'outlook-mail'; sourceId: string; userId?: string; subject?: string }[]): FileUploaderOutlookMailRow[] {
        const rows: FileUploaderOutlookMailRow[] = [];

        for (let item of items) {
            if (this._multiple && this.outlookMailRows.some((row) => row.sourceId === item.sourceId && row.userId === item.userId)) {
                continue;
            }

            const row: FileUploaderOutlookMailRow = {
                kind: 'outlook-mail',
                rowId: crypto.randomUUID(),
                sourceId: item.sourceId,
                userId: item.userId,
                subject: item.subject,
                status: 'loading',
            };

            this.outlookMailRows.push(row);
            rows.push(row);
        }

        return rows;
    }

    private addOutlookAttachmentRows(items: FileUploaderDroppedOutlookAttachmentItem[]): FileUploaderOutlookAttachmentRow[] {
        const rows: FileUploaderOutlookAttachmentRow[] = [];

        for (let item of items) {
            const matchingMailAttachment = this.getMatchingOutlookMailAttachment(item);

            if (!!matchingMailAttachment) {
                if (matchingMailAttachment.attachmentRow.removed) {
                    this.restoreOutlookMailAttachment(matchingMailAttachment.mailRow, matchingMailAttachment.attachmentRow);
                }

                continue;
            }

            if (this._multiple && this.isOutlookAttachmentAlreadySelected(item)) {
                continue;
            }

            const row: FileUploaderOutlookAttachmentRow = {
                kind: 'outlook-attachment',
                rowId: crypto.randomUUID(),
                attachmentItemId: item.attachmentItemId,
                userId: item.userId,
                name: item.name,
                size: item.size,
                contentType: item.contentType,
                status: 'loading',
            };

            this.outlookAttachmentRows.push(row);
            rows.push(row);
        }

        return rows;
    }

    private isOutlookAttachmentAlreadySelected(item: FileUploaderDroppedOutlookAttachmentItem): boolean {
        return this.outlookAttachmentRows.some((row) => row.attachmentItemId === item.attachmentItemId && row.userId === item.userId) || this.outlookAttachmentFileMetadata.some((metadata) => this.isDroppedOutlookAttachmentMatchingStandaloneMetadata(item, metadata)) || this.files.some((file) => file.name === item.name);
    }

    private getMatchingOutlookMailAttachment(item: FileUploaderDroppedOutlookAttachmentItem): { mailRow: FileUploaderOutlookMailRow; attachmentRow: FileUploaderOutlookMailAttachmentRow } | undefined {
        for (let mailRow of this.outlookMailRows) {
            if (!this.isSameOptionalMailbox(mailRow.userId, item.userId)) {
                continue;
            }

            const attachmentRow = mailRow.attachments?.find((mailAttachmentRow) => this.isDroppedOutlookAttachmentMatchingMailAttachment(item, mailAttachmentRow));

            if (!!attachmentRow) {
                return { mailRow: mailRow, attachmentRow: attachmentRow };
            }
        }

        return undefined;
    }

    private getMatchingStandaloneOutlookAttachmentFiles(mailRow: FileUploaderOutlookMailRow): FileUploaderOutlookAttachmentFileMetadata[] {
        return this.outlookAttachmentFileMetadata.filter((metadata) => this.isSameOptionalMailbox(metadata.userId, mailRow.userId) && !!mailRow.attachments?.some((attachmentRow) => this.isStandaloneMetadataMatchingMailAttachment(metadata, attachmentRow)));
    }

    private absorbStandaloneOutlookAttachmentFiles(mailRow: FileUploaderOutlookMailRow) {
        const matchingFiles = this.getMatchingStandaloneOutlookAttachmentFiles(mailRow);

        if (matchingFiles.length < 1) {
            return;
        }

        this.files = this.files.filter((file) => !matchingFiles.some((metadata) => metadata.file === file));
        this.outlookAttachmentFileMetadata = this.outlookAttachmentFileMetadata.filter((metadata) => !matchingFiles.includes(metadata));
    }

    private isDroppedOutlookAttachmentMatchingStandaloneMetadata(item: FileUploaderDroppedOutlookAttachmentItem, metadata: FileUploaderOutlookAttachmentFileMetadata): boolean {
        if (!this.isSameOptionalMailbox(item.userId, metadata.userId)) {
            return false;
        }

        return this.areNonEmptyStringsEqual(item.attachmentItemId, metadata.sourceAttachmentItemId) || this.isOutlookAttachmentShapeMatch({ name: item.name, size: item.size, contentType: item.contentType }, metadata);
    }

    private isDroppedOutlookAttachmentMatchingMailAttachment(item: FileUploaderDroppedOutlookAttachmentItem, attachmentRow: FileUploaderOutlookMailAttachmentRow): boolean {
        const attachmentId = this.getAttachmentId(attachmentRow.attachment);

        return this.areNonEmptyStringsEqual(item.attachmentItemId, attachmentId) || this.isOutlookAttachmentShapeMatch({ name: item.name, size: item.size, contentType: item.contentType }, this.getOutlookMailAttachmentMatchData(attachmentRow));
    }

    private isStandaloneMetadataMatchingMailAttachment(metadata: FileUploaderOutlookAttachmentFileMetadata, attachmentRow: FileUploaderOutlookMailAttachmentRow): boolean {
        const attachmentId = this.getAttachmentId(attachmentRow.attachment);

        return this.areNonEmptyStringsEqual(metadata.attachmentId, attachmentId) || this.areNonEmptyStringsEqual(metadata.sourceAttachmentItemId, attachmentId) || this.isOutlookAttachmentShapeMatch(metadata, this.getOutlookMailAttachmentMatchData(attachmentRow));
    }

    private getOutlookMailAttachmentMatchData(attachmentRow: FileUploaderOutlookMailAttachmentRow): OutlookAttachmentMatchData {
        return {
            name: attachmentRow.name,
            size: attachmentRow.attachment.size,
            contentType: this.getAttachmentContentType(attachmentRow.attachment),
        };
    }

    private isOutlookAttachmentShapeMatch(first: OutlookAttachmentMatchData, second: OutlookAttachmentMatchData): boolean {
        if (!this.areNonEmptyStringsEqual(first.name, second.name)) {
            return false;
        }

        const firstSize = this.getOptionalNumber(first.size);
        const secondSize = this.getOptionalNumber(second.size);
        const firstContentType = this.normalizeOptionalString(first.contentType);
        const secondContentType = this.normalizeOptionalString(second.contentType);
        const hasSizeComparison = firstSize !== undefined && secondSize !== undefined;
        const hasContentTypeComparison = !!firstContentType && !!secondContentType;

        if (hasSizeComparison && firstSize !== secondSize) {
            return false;
        }

        if (hasContentTypeComparison && firstContentType !== secondContentType) {
            return false;
        }

        return hasSizeComparison || hasContentTypeComparison;
    }

    private isSameOptionalMailbox(first?: string, second?: string): boolean {
        return !first || !second || this.areNonEmptyStringsEqual(first, second);
    }

    private areNonEmptyStringsEqual(first?: string | null, second?: string | null): boolean {
        const firstNormalized = this.normalizeOptionalString(first);
        const secondNormalized = this.normalizeOptionalString(second);

        return !!firstNormalized && !!secondNormalized && firstNormalized === secondNormalized;
    }

    private normalizeOptionalString(value?: string | null): string | undefined {
        return typeof value === 'string' && value.trim().length > 0 ? value.trim().toLocaleLowerCase() : undefined;
    }

    private getOptionalNumber(value?: number): number | undefined {
        return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
    }

    private addOutlookAttachmentFileMetadata(file: File, attachment: FileAttachment, row: FileUploaderOutlookAttachmentRow) {
        if (!this.files.includes(file)) {
            return;
        }

        this.outlookAttachmentFileMetadata.push({
            file: file,
            sourceAttachmentItemId: row.attachmentItemId,
            attachmentId: attachment.id,
            userId: row.userId,
            name: file.name,
            size: this.getOptionalNumber(attachment.size) ?? row.size ?? file.size,
            contentType: this.firstNonEmptyString(attachment.contentType ?? undefined, row.contentType, file.type),
        });
    }

    private retryFailedOutlookMailRows() {
        const failedRows = this.outlookMailRows.filter((row) => row.status === 'error');
        const failedAttachmentRows = this.outlookAttachmentRows.filter((row) => row.status === 'error');

        if (failedRows.length > 0) {
            failedRows.forEach((row) => this.resetOutlookMailRowForRetry(row));
            void this.loadOutlookMailRows(failedRows);
        }

        if (failedAttachmentRows.length > 0) {
            failedAttachmentRows.forEach((row) => this.resetOutlookAttachmentRowForRetry(row));
            void this.loadOutlookAttachmentRows(failedAttachmentRows);
        }
    }

    private resetOutlookMailRowForRetry(row: FileUploaderOutlookMailRow) {
        const currentRow = this.getOutlookMailRow(row.rowId);

        if (!currentRow) {
            return;
        }

        currentRow.status = 'loading';
        delete currentRow.errorMessage;
        delete currentRow.targetId;
        delete currentRow.message;
        delete currentRow.attachments;
    }

    private resetOutlookAttachmentRowForRetry(row: FileUploaderOutlookAttachmentRow) {
        const currentRow = this.getOutlookAttachmentRow(row.rowId);

        if (!currentRow) {
            return;
        }

        currentRow.status = 'loading';
        delete currentRow.errorMessage;
    }

    private async loadOutlookMailRows(rows: FileUploaderOutlookMailRow[]) {
        const rowsByUserId = this.groupOutlookMailRowsByUserId(rows);

        await Promise.all(Array.from(rowsByUserId.values()).map((userRows) => this.loadOutlookMailRowsForUser(userRows)));
    }

    private groupOutlookMailRowsByUserId(rows: FileUploaderOutlookMailRow[]): Map<string, FileUploaderOutlookMailRow[]> {
        const rowsByUserId = new Map<string, FileUploaderOutlookMailRow[]>();

        rows.forEach((row) => {
            const key = row.userId ?? '';
            rowsByUserId.set(key, [...(rowsByUserId.get(key) ?? []), row]);
        });

        return rowsByUserId;
    }

    private async loadOutlookMailRowsForUser(rows: FileUploaderOutlookMailRow[]) {
        const userId = rows[0]?.userId;

        try {
            const exchangedIds = await this.graphMail.translateExchangeIds({ inputIds: rows.map((row) => row.sourceId), sourceIdType: 'ewsId', targetIdType: 'restId', ...(!!userId ? { userId: userId } : {}) });

            if (!exchangedIds) throw Error('Cannot access mail information');

            await Promise.all(
                rows.map(async (row) => {
                    const currentRow = this.getOutlookMailRow(row.rowId);

                    if (!currentRow) {
                        return;
                    }

                    const translatedId = exchangedIds.value.find((exchangeId) => exchangeId.sourceId === row.sourceId);

                    if (!translatedId?.targetId) {
                        this.setOutlookMailRowError(currentRow, 'Could not translate Outlook email id');
                        return;
                    }

                    currentRow.targetId = translatedId.targetId;

                    try {
                        const message = await this.graphMail.getMessage(translatedId.targetId, row.userId);
                        const enrichedMessage = await this.getMessageWithInlineAttachmentsReplaced(message, translatedId.targetId, row.userId);
                        const latestRow = this.getOutlookMailRow(row.rowId);

                        if (!latestRow) {
                            return;
                        }

                        latestRow.message = enrichedMessage.message;
                        latestRow.attachments = enrichedMessage.attachments;
                        this.absorbStandaloneOutlookAttachmentFiles(latestRow);
                        this.syncOutlookMailMessageAttachments(latestRow);
                        latestRow.status = 'loaded';
                        this.emitFilesChange();
                    } catch (error) {
                        console.dir(error);
                        this.setOutlookMailRowError(currentRow, error);
                    }
                }),
            );
        } catch (error) {
            console.dir(error);
            let message = '';

            if (('' + error).includes('no_account_error')) {
                message = this.fileTranslations.no_account_error;
            } else if (!!error && typeof error === 'object' && 'code' in error && typeof error.code === 'string' && error.code.includes('ErrorInvalidUser')) {
                message = this.fileTranslations.invalid_user_error;
            } else {
                message = '' + error;
            }

            rows.forEach((row) => this.setOutlookMailRowError(row, message));
        }
    }

    private async loadOutlookAttachmentRows(rows: FileUploaderOutlookAttachmentRow[]) {
        await Promise.all(rows.map((row) => this.loadOutlookAttachmentRow(row)));
    }

    private async loadOutlookAttachmentRow(row: FileUploaderOutlookAttachmentRow) {
        try {
            const currentRow = this.getOutlookAttachmentRow(row.rowId);

            if (!currentRow) {
                return;
            }

            const attachment = await this.graphMail.getDroppedFileAttachmentFromItemId({
                attachmentItemId: row.attachmentItemId,
                name: row.name,
                size: row.size,
                contentType: row.contentType,
                userId: row.userId,
            });
            const latestRow = this.getOutlookAttachmentRow(row.rowId);

            if (!latestRow) {
                return;
            }

            const file = this.createFileFromGraphFileAttachment(attachment, latestRow);
            const matchingMailAttachment = this.getMatchingOutlookMailAttachment({
                kind: 'outlook-attachment',
                attachmentItemId: latestRow.attachmentItemId,
                name: latestRow.name,
                size: latestRow.size,
                contentType: latestRow.contentType,
                userId: latestRow.userId,
            });

            if (!!matchingMailAttachment) {
                this.outlookAttachmentRows = this.outlookAttachmentRows.filter((attachmentRow) => attachmentRow.rowId !== latestRow.rowId);

                if (matchingMailAttachment.attachmentRow.removed) {
                    this.restoreOutlookMailAttachment(matchingMailAttachment.mailRow, matchingMailAttachment.attachmentRow);
                }

                return;
            }

            if (!this.isFileTypeAccepted(file)) {
                this.setOutlookAttachmentRowError(latestRow, 'File type not accepted');
                return;
            }

            this.outlookAttachmentRows = this.outlookAttachmentRows.filter((attachmentRow) => attachmentRow.rowId !== latestRow.rowId);
            this.updateFilesList([file]);
            this.addOutlookAttachmentFileMetadata(file, attachment, latestRow);
            this.emitFilesChange();
        } catch (error) {
            this.setOutlookAttachmentRowError(row, error);
        }
    }

    private createFileFromGraphFileAttachment(attachment: FileAttachment, row: FileUploaderOutlookAttachmentRow): File {
        const contentBytes = attachment.contentBytes;

        if (!contentBytes) {
            throw Error(`Outlook attachment ${row.name} has no file content`);
        }

        const blobData = atob(contentBytes);
        const byteNumbers = new Array(blobData.length);

        for (let i = 0; i < blobData.length; i++) {
            byteNumbers[i] = blobData.charCodeAt(i);
        }

        return new File([new Uint8Array(byteNumbers)], attachment.name ?? row.name, { type: attachment.contentType ?? row.contentType ?? 'application/octet-stream' });
    }

    private async getMessageWithInlineAttachmentsReplaced(message: Message, messageId: string, userId?: string): Promise<{ message: Message; attachments: FileUploaderOutlookMailAttachmentRow[] }> {
        const attachments = await this.getMessageAttachments(messageId, userId);
        const htmlContent = message.body?.content ?? '';
        const inlineAttachments = attachments.filter((attachment) => this.isInlineAttachmentForHtml(attachment, htmlContent));
        const nonInlineAttachments = attachments.filter((attachment) => !inlineAttachments.includes(attachment));
        const enrichedMessage = this.replaceMessageInlineAttachmentsWithContentBytes(message, attachments, nonInlineAttachments);

        return {
            message: enrichedMessage,
            attachments: this.createOutlookMailAttachmentRows(nonInlineAttachments),
        };
    }

    private async getMessageAttachments(messageId: string, userId?: string): Promise<FileUploaderAttachment[]> {
        return (await this.graphMail.getAttachmentsFromMessageId(messageId, userId))?.value ?? [];
    }

    private createOutlookMailAttachmentRows(attachments: FileUploaderAttachment[]): FileUploaderOutlookMailAttachmentRow[] {
        return attachments.map((attachment, index) => ({
            rowId: crypto.randomUUID(),
            attachment: attachment,
            name: this.getAttachmentName(attachment, index),
        }));
    }

    private replaceMessageInlineAttachmentsWithContentBytes(message: Message, attachments: FileUploaderAttachment[], nonInlineAttachments: FileUploaderAttachment[]): Message {
        const htmlContent = message.body?.content;

        return {
            ...message,
            body: !!htmlContent ? { ...message.body, content: this.replaceInlineAttachmentsWithContentBytes(htmlContent, attachments) } : message.body,
            attachments: nonInlineAttachments as Message['attachments'],
        };
    }

    private replaceInlineAttachmentsWithContentBytes(htmlContent: string, attachments: FileUploaderAttachment[], sizeLimit = true): string {
        let html = htmlContent;

        attachments.forEach((attachment) => {
            const replacement = this.getInlineAttachmentReplacement(attachment, sizeLimit);

            if (!replacement) {
                return;
            }

            for (let cidReference of this.getCidReferenceCandidates(attachment)) {
                html = html.replaceAll(cidReference, replacement);
            }
        });

        return html;
    }

    private isInlineAttachmentForHtml(attachment: FileUploaderAttachment, htmlContent: string): boolean {
        return this.isInlineAttachment(attachment) || this.getCidReferenceCandidates(attachment).some((cidReference) => htmlContent.includes(cidReference));
    }

    private isInlineAttachment(attachment: unknown): boolean {
        return !!attachment && typeof attachment === 'object' && 'isInline' in attachment && (attachment as { isInline?: unknown }).isInline === true;
    }

    private getInlineAttachmentReplacement(attachment: FileUploaderAttachment, sizeLimit: boolean): string | null {
        const contentId = this.getAttachmentContentId(attachment);

        if (!contentId) {
            return null;
        }

        if (sizeLimit && (attachment.size ?? 0) >= 10000000) {
            return `cid:${contentId}" alt="Image size too big`;
        }

        const contentBytes = this.getAttachmentContentBytes(attachment);

        if (!contentBytes) {
            return null;
        }

        return `data:${this.getAttachmentContentType(attachment)};base64,${contentBytes}`;
    }

    private getCidReferenceCandidates(attachment: FileUploaderAttachment): string[] {
        const contentId = this.getAttachmentContentId(attachment);

        if (!contentId) {
            return [];
        }

        const trimmedContentId = contentId.trim();
        const contentIdWithoutBrackets = trimmedContentId.replace(/^<|>$/g, '');
        const candidates = [trimmedContentId, contentIdWithoutBrackets, encodeURIComponent(trimmedContentId), encodeURIComponent(contentIdWithoutBrackets)];

        return [...new Set(candidates.filter((candidate) => candidate.length > 0).map((candidate) => `cid:${candidate}`))];
    }

    private getAttachmentContentId(attachment: FileUploaderAttachment): string | null {
        if ('contentId' in attachment && typeof attachment.contentId === 'string' && attachment.contentId.trim().length > 0) {
            return attachment.contentId.trim();
        }

        return null;
    }

    private getAttachmentContentBytes(attachment: FileUploaderAttachment): string | null {
        if ('contentBytes' in attachment && typeof attachment.contentBytes === 'string' && attachment.contentBytes.length > 0) {
            return attachment.contentBytes;
        }

        return null;
    }

    private getAttachmentContentType(attachment: FileUploaderAttachment): string {
        if ('contentType' in attachment && typeof attachment.contentType === 'string' && attachment.contentType.trim().length > 0) {
            return attachment.contentType.trim();
        }

        return 'application/octet-stream';
    }

    private getAttachmentName(attachment: unknown, index: number): string {
        if (!!attachment && typeof attachment === 'object' && 'name' in attachment && typeof (attachment as { name?: unknown }).name === 'string') {
            const name = (attachment as { name: string }).name.trim();

            if (name.length > 0) {
                return name;
            }
        }

        return `Attachment ${index + 1}`;
    }

    private getAttachmentId(attachment: unknown): string | undefined {
        if (!!attachment && typeof attachment === 'object' && 'id' in attachment && typeof (attachment as { id?: unknown }).id === 'string') {
            return (attachment as { id: string }).id;
        }

        return undefined;
    }

    private syncOutlookMailMessageAttachments(row: FileUploaderOutlookMailRow) {
        if (!row.message) {
            return;
        }

        row.message = {
            ...row.message,
            attachments: (row.attachments ?? []).filter((attachmentRow) => !attachmentRow.removed).map((attachmentRow) => attachmentRow.attachment) as Message['attachments'],
        };
    }

    private getOutlookMailRow(rowId: string): FileUploaderOutlookMailRow | undefined {
        return this.outlookMailRows.find((row) => row.rowId === rowId);
    }

    private getOutlookAttachmentRow(rowId: string): FileUploaderOutlookAttachmentRow | undefined {
        return this.outlookAttachmentRows.find((row) => row.rowId === rowId);
    }

    private setOutlookMailRowError(row: FileUploaderOutlookMailRow, error: unknown) {
        const currentRow = this.getOutlookMailRow(row.rowId);

        if (!currentRow) {
            return;
        }

        currentRow.status = 'error';
        currentRow.errorMessage = this.getErrorMessage(error);
    }

    private setOutlookAttachmentRowError(row: FileUploaderOutlookAttachmentRow, error: unknown) {
        const currentRow = this.getOutlookAttachmentRow(row.rowId);

        if (!currentRow) {
            return;
        }

        currentRow.status = 'error';
        currentRow.errorMessage = this.getErrorMessage(error);
    }

    private getOutlookMailRowSubject(row: FileUploaderOutlookMailRow, fallback: string): string {
        return this.firstNonEmptyString(row.message?.subject ?? undefined, row.subject, fallback) ?? fallback;
    }

    private getErrorMessage(error: unknown): string {
        if (error instanceof Error && error.message) {
            return error.message;
        }

        if (!!error && typeof error === 'object' && 'message' in error && typeof (error as { message?: unknown }).message === 'string') {
            return (error as { message: string }).message;
        }

        if (typeof error === 'string' && error.trim().length > 0) {
            return error;
        }

        return 'Unknown error';
    }

    private getSelectedValues(): TValue[] {
        return [...this.files, ...this.outlookMailRows.filter((row) => row.status === 'loaded' && !!row.message).map((row) => row.message!)] as TValue[];
    }

    private emitFilesChange() {
        this.ttFiles = this.getSelectedValues();
        this.ttFilesChange.emit(this.ttFiles);
    }

    private syncSelectedValues(values: TValue[]) {
        const selectedValues = (Array.isArray(values) ? values : []) as FileUploaderValue[];

        this.files = selectedValues.filter((value): value is File => this.isFileValue(value));
        this.outlookAttachmentFileMetadata = this.outlookAttachmentFileMetadata.filter((metadata) => this.files.includes(metadata.file));
        this.syncOutlookMailRowsFromMessages(selectedValues.filter((value): value is Message => this.isGraphMessageValue(value)));
    }

    private syncOutlookMailRowsFromMessages(messages: Message[]) {
        const rowsWithoutLoadedMessages = this.outlookMailRows.filter((row) => row.status !== 'loaded' || !row.message);
        const loadedMessageRows = messages.map((message) => this.getExistingOutlookMailRowForMessage(message) ?? this.createLoadedOutlookMailRow(message));

        this.outlookMailRows = [...rowsWithoutLoadedMessages, ...loadedMessageRows];
    }

    private getExistingOutlookMailRowForMessage(message: Message): FileUploaderOutlookMailRow | undefined {
        return this.outlookMailRows.find((row) => row.status === 'loaded' && !!row.message && this.isSameGraphMessage(row.message, message));
    }

    private createLoadedOutlookMailRow(message: Message): FileUploaderOutlookMailRow {
        return {
            kind: 'outlook-mail',
            rowId: crypto.randomUUID(),
            sourceId: message.id ?? crypto.randomUUID(),
            targetId: message.id,
            subject: message.subject ?? undefined,
            message: message,
            attachments: this.createOutlookMailAttachmentRows(this.getMessageAttachmentArray(message)),
            status: 'loaded',
        };
    }

    private getMessageAttachmentArray(message: Message): FileUploaderAttachment[] {
        return Array.isArray(message.attachments) ? (message.attachments as FileUploaderAttachment[]) : [];
    }

    private isSameGraphMessage(first: Message, second: Message): boolean {
        if (first === second) {
            return true;
        }

        return !!first.id && first.id === second.id;
    }

    private areSelectedValuesEqual(first: TValue[], second: TValue[]): boolean {
        return first.length === second.length && first.every((value, index) => value === second[index] || (this.isGraphMessageValue(value) && this.isGraphMessageValue(second[index]) && this.isSameGraphMessage(value, second[index] as Message)));
    }

    private isFileValue(value: unknown): value is File {
        return value instanceof File;
    }

    private isGraphMessageValue(value: unknown): value is Message {
        return !!value && typeof value === 'object' && !this.isFileValue(value) && ('id' in value || 'subject' in value || 'body' in value || 'from' in value);
    }

    /**
     * Updates the list of files with the given list of files, keeping filenames unique.
     *
     * @param files the files to update the file list in the component with.
     * @emits ttFilesChange
     */
    private updateFilesList(files: File[]) {
        if (this._multiple) {
            for (let file of files) {
                if (!this.files.map((file) => file.name).includes(file.name)) {
                    this.files.push(file);
                }
            }
        } else {
            this.files = [files![0]];
            this.outlookAttachmentFileMetadata = this.outlookAttachmentFileMetadata.filter((metadata) => metadata.file === files![0]);
        }
    }

    /**
     * Empties the files in the input field itself.
     */
    private resetFileInput() {
        if (!!this.inputRef?.nativeElement) {
            this.inputRef.nativeElement.value = '';
        }
    }

    /**
     * Removes the given file from the list of selected files.
     *
     * @param file the file to remove.
     * @emits ttFileRemoved
     * @emits ttFilesChange
     */
    public removeFile(file: File) {
        const index = this.files.indexOf(file);

        if (index < 0) {
            return;
        }

        this.files.splice(index, 1);
        this.outlookAttachmentFileMetadata = this.outlookAttachmentFileMetadata.filter((metadata) => metadata.file !== file);
        this.resetFileInput();
        this.emitFilesChange();
    }

    private removeOutlookMailRow(row: FileUploaderOutlookMailRow) {
        const selectedValuesChanged = row.status === 'loaded' && !!row.message;

        this.outlookMailRows = this.outlookMailRows.filter((mailRow) => mailRow.rowId !== row.rowId);

        if (selectedValuesChanged) {
            this.emitFilesChange();
        }
    }

    private removeOutlookAttachmentRow(row: FileUploaderOutlookAttachmentRow) {
        this.outlookAttachmentRows = this.outlookAttachmentRows.filter((attachmentRow) => attachmentRow.rowId !== row.rowId);
    }

    /**
     * Translates the words used in component.
     */
    private async translate() {
        const translations = await this.translateService.translateBatch(Object.keys(this.filetranslations));

        Object.keys(translations).forEach((key) => {
            if (translations[key]) {
                this.filetranslations[key] = translations[key];
            }
        });
    }

    override ngOnInit(): void {
        this.translate();
        // this.layoutService.layoutChanged.subscribe((info) => {
        //     if (info) {
        //         this.style['dropzone_label'].fontSize = info.fontSizes.textSize;
        //     }
        // });
    }

    override async ngOnChanges(changes: SimpleChanges): Promise<void> {
        super.ngOnChanges(changes);

        if (changes['ttAllowEmails']) {
            this.ttAllowEmails = changes['ttAllowEmails'].currentValue;
        }

        if (changes?.['ttFiles']?.currentValue) {
            const selectedValues = [...changes['ttFiles'].currentValue] as TValue[];

            if (!this.areSelectedValuesEqual(selectedValues, this.getSelectedValues())) {
                this.syncSelectedValues(selectedValues);
            }

            this.resetFileInput();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}

interface FileUploaderFileRow {
    kind: 'file';
    file: File;
}

interface FileUploaderOutlookMailRow {
    kind: 'outlook-mail';
    rowId: string;
    sourceId: string;
    userId?: string;
    subject?: string;
    targetId?: string;
    message?: Message;
    attachments?: FileUploaderOutlookMailAttachmentRow[];
    status: 'loading' | 'loaded' | 'error';
    errorMessage?: string;
}

interface FileUploaderOutlookMailAttachmentRow {
    rowId: string;
    attachment: FileUploaderAttachment;
    name: string;
    removed?: boolean;
}

interface FileUploaderOutlookAttachmentRow {
    kind: 'outlook-attachment';
    rowId: string;
    attachmentItemId: string;
    userId?: string;
    name: string;
    size?: number;
    contentType?: string;
    status: 'loading' | 'loaded' | 'error';
    errorMessage?: string;
}

interface FileUploaderOutlookAttachmentFileMetadata extends OutlookAttachmentMatchData {
    file: File;
    sourceAttachmentItemId: string;
    attachmentId?: string;
    userId?: string;
}

interface OutlookAttachmentMatchData {
    name: string;
    size?: number;
    contentType?: string;
}

type FileUploaderDroppedOutlookAttachmentItem = { kind: 'outlook-attachment'; attachmentItemId: string; name: string; size?: number; contentType?: string; userId?: string };

type FileUploaderDroppedItem = { kind: 'file'; file: File } | { kind: 'outlook-mail'; sourceId: string; userId?: string; subject?: string } | FileUploaderDroppedOutlookAttachmentItem;

type OutlookDroppedPayload = OutlookDroppedMailPayload | OutlookDroppedAttachmentPayload;

interface OutlookDroppedMailPayload {
    itemIds?: string[];
    latestItemIds?: string[];
    mailboxInfos?: OutlookMailboxInfo[];
    subjects: string[];
    [key: string]: unknown;
    itemData?: OutlookDroppedMailItemData;
}

interface OutlookDroppedAttachmentPayload {
    itemType: 'attachment';
    attachmentFiles: OutlookDroppedAttachmentFile[];
    mailboxInfos?: OutlookMailboxInfo[];
    [key: string]: unknown;
    itemData?: OutlookDroppedMailItemData;
}

interface OutlookDroppedAttachmentFile {
    attachmentItemId: string;
    name: string;
    size?: number;
    ContentType?: string;
    contentType?: string;
    fileType?: number;
    type?: string;
    [key: string]: unknown;
}

interface OutlookDroppedMailItemData {
    mailboxInfo: OutlookMailboxInfo;
}

interface OutlookMailboxInfo {
    sourceId: string;
    type: string;
    mailboxSmtpAddress: string;
    userIdentity: string;
    mailboxRank: string;
    diagnosticData: string;
    mailboxProvider: string;
    isExplicitLogon: false;
}
